import * as Yup from "yup";
import { FormikProvider, getIn, useFormik } from "formik";
import { IRenter } from "../../../../types/renter.type";
import { useEffect, useState } from "react";
import { ICompanyAccess } from "../../../../types/companyaccess.type";
import { getCompanies } from "../../../../common/services/company.api";
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import CpfDoIndicadorCheck from "../../../leads/components/forms/fields/CpfDoIndicadorCheck";
import { isCPF } from "../../../../common/helpers/is-cpf";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { ConsultantOption } from "@/types/employee.type";
import { getConsultantOptionsFromAPI } from "@/common/services/employee.api";

const validationSchema = Yup.object().shape({
  cpfDoIndicador: Yup.string().when("cpfDoIndicadorOptions.indicated", {
    is: true,
    then: (schema) =>
      schema
        .required("CPF do Indicador é obrigatório")
        .test("valida-cpf", "CPF inválido", (value) => isCPF(value)),
  }),
});

type FormPropsActionCommercial = {
  data: IRenter;
  onSubmit: (
    values: Partial<IRenter>
  ) => Promise<{ ok: boolean; data: Partial<IRenter>; errors?: unknown[] }>;
  formRequestErrors?: [{ message: string; path: string[] }];
};

function FormLeadCommecialActions(props: FormPropsActionCommercial) {
  const renter = props.data;
  const [companies, setCompanies] = useState<ICompanyAccess[]>([]);
  const [consultantOptions, setConsultantOptions] = useState<
    ConsultantOption[]
  >([]);

  useEffect(() => {
    async function loadCompanies() {
      const data = await getCompanies();
      setCompanies(data.companies);
    }
    loadCompanies();
  }, []);

  const theInitialData = {
    cpfDoIndicadorOptions: {
      indicated: renter?.cpfDoIndicadorOptions?.indicated || false,
      notIndicated: renter?.cpfDoIndicadorOptions?.notIndicated || false,
    },
    companyId: renter.companyId || "",
    technicalConsultant: renter.technicalConsultant || "",
    cpfDoIndicador: renter.cpfDoIndicador || "",
  };
  const formik = useFormik({
    initialValues: theInitialData,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const { ok, data, errors } = await props.onSubmit(values);

        return {
          ok,
          data: data as Partial<IRenter>,
          errors: errors as [{ message: string; path: string[] }],
        };
      } catch (error) {
        console.error("Erro ao salvar os dados:", error);

        return {
          ok: false,
          data: values,
          errors: [
            {
              message:
                error instanceof Error
                  ? error.message
                  : "Erro desconhecido ao processar sua solicitação",
              path: [""],
            },
          ],
        };
      }
    },
  });
  useEffect(() => {
    const fetchConsultants = async () => {
      const selectedCompanyId = formik.values.companyId;

      const options = await getConsultantOptionsFromAPI(
        selectedCompanyId ? [selectedCompanyId] : undefined,
        undefined
      );

      // Garante que o valor salvo inicialmente continue na lista
      const savedValue = props.data.technicalConsultant;
      if (savedValue && !options.some((opt) => opt.value === savedValue)) {
        options.unshift({ value: savedValue, label: savedValue });
      }

      setConsultantOptions(options);
    };

    fetchConsultants();
  }, [formik.values.companyId, props.data.technicalConsultant]);

  const invalidCpf = getIn(formik.errors, "cpfDoIndicador");

  return (
    <form className="form-edit" onSubmit={formik.handleSubmit}>
      <section className="flex flex-col gap-4">
        <FormikProvider value={formik}>
          <section>
            <CpfDoIndicadorCheck invalidCpf={invalidCpf} />
          </section>
          <div className="flex flex-col sm:flex-row gap-3">
            <ComboBoxFormik
              required={true}
              name="companyId"
              label="Base:"
              options={companies
                .filter((company) => company.companyId && company.companyName) // Filter out invalid entries
                .map((company) => ({
                  value: company.companyId as string, // Now guaranteed to be string
                  label: company.companyName as string,
                }))}
              className="w-full"
            />
          </div>

          <ComboBoxFormik
            label="Consultor:"
            name="technicalConsultant"
            options={consultantOptions}
            required={true}
          />

          <div className="w-full flex justify-end">
            <ButtonWithLoader
              isLoading={formik.isSubmitting}
              disabled={!formik.isValid}
              onClick={formik.handleSubmit}
            >
              salvar
            </ButtonWithLoader>
          </div>
        </FormikProvider>
      </section>
    </form>
  );
}

export default FormLeadCommecialActions;
